import type * as React from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from './skeleton';

type StatNoteTone = 'muted' | 'success' | 'warning' | 'danger';

const NOTE_TONE: Readonly<Record<StatNoteTone, string>> = {
  muted: 'text-muted-foreground',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
};

/**
 * Stat tile (DESIGN_SYSTEM §4, T-093): sunken well, radius lg; 12 px muted label, tabular
 * value in the display face (md 24/32 for tile grids, lg = `text-stat` 32/40), optional note
 * line in a tone. Marked up as a description list so the label names the value.
 */
function StatTile({
  label,
  value,
  note,
  noteTone = 'muted',
  size = 'md',
  loading = false,
  className,
  ...props
}: Omit<React.ComponentProps<'dl'>, 'children'> & {
  label: React.ReactNode;
  value: React.ReactNode;
  note?: React.ReactNode;
  noteTone?: StatNoteTone;
  size?: 'md' | 'lg';
  loading?: boolean;
}) {
  return (
    <dl
      data-slot="stat-tile"
      aria-busy={loading || undefined}
      className={cn(
        'flex min-w-0 flex-col rounded-lg bg-surface-muted px-3.5 py-3 text-foreground',
        className,
      )}
      {...props}
    >
      <dt className="truncate text-caption text-muted-foreground">{label}</dt>
      <dd className={size === 'lg' ? 'text-stat' : 'text-stat-sm'}>
        {loading ? (
          <Skeleton className={cn('my-1 w-16 bg-border', size === 'lg' ? 'h-8' : 'h-6')} />
        ) : (
          value
        )}
      </dd>
      {note !== undefined && note !== null && !loading ? (
        <dd className={cn('text-caption', NOTE_TONE[noteTone])}>{note}</dd>
      ) : null}
    </dl>
  );
}

export { StatTile, type StatNoteTone };
